import { useEffect, useState } from "react";
import { Printer, RefreshCw, Store } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { loadPrinterCfg, printOrder, qzListPrinters, savePrinterCfg } from "@/lib/printer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const card = "space-y-4 rounded-2xl border border-white/[0.08] bg-[#1A1A1A] p-6";
const SAMPLE = {
  number: 0, created_at: new Date().toISOString(), fulfillment: "entrega", customer_name: "Cliente Teste", phone: "(00) 00000-0000",
  address: "Rua Exemplo, 123", notes: "", items: [{ product_id: "x", name: "Cerveja Pilsen Lata 350ml", qty: 6, subtotal: 27 }],
  subtotal: 27, delivery_fee: 5, total: 32, payment_method: "dinheiro", change_for: 50,
};

function StoreSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/admin/settings", { ...s, delivery_fee: Number(s.delivery_fee), min_order_delivery: Number(s.min_order_delivery || 0) }); setS(data); toast.success("Configurações salvas"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className={card}>
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Store className="h-5 w-5 text-primo-400" /> Loja</h2>
      <div className="flex items-center justify-between rounded-xl bg-black/20 p-3">
        <div><p className="font-medium">Loja aberta</p><p className="text-xs text-slate-500">Quando fechada, clientes não conseguem enviar pedidos</p></div>
        <Switch data-testid="store-open-switch" checked={s.store_open} onCheckedChange={(v) => setS((p) => ({ ...p, store_open: v }))} />
      </div>
      <div><Label>Taxa de entrega fixa (R$)</Label><Input data-testid="delivery-fee-input" type="number" min={0} step="0.01" value={s.delivery_fee} onChange={set("delivery_fee")} /></div>
      <div><Label>Pedido mínimo para entrega (R$)</Label><Input data-testid="min-order-delivery-input" type="number" min={0} step="0.01" value={s.min_order_delivery ?? 0} onChange={set("min_order_delivery")} />
        <p className="mt-1 text-xs text-slate-500">Abaixo desse valor só é possível retirar na loja</p></div>
      <div><Label>Endereço da loja (aparece no cupom)</Label><Input data-testid="store-address-input" value={s.store_address} onChange={set("store_address")} /></div>
      <div><Label>Telefone da loja</Label><Input data-testid="store-phone-input" value={s.store_phone} onChange={set("store_phone")} /></div>
      <div><Label>Chave Pix (mostrada ao cliente)</Label><Input data-testid="pix-key-input" value={s.pix_key} onChange={set("pix_key")} /></div>
      <Button data-testid="save-store-settings-button" className="rounded-full font-bold">Salvar loja</Button>
    </form>
  );
}

function PrinterSettings() {
  const [cfg, setCfg] = useState(loadPrinterCfg);
  const [printers, setPrinters] = useState([]);
  const [searching, setSearching] = useState(false);
  const update = (patch) => setCfg((c) => { const n = { ...c, ...patch }; savePrinterCfg(n); return n; });

  const search = async () => {
    setSearching(true);
    try { const list = await qzListPrinters(); setPrinters(list); toast.success(`${list.length} impressora(s) encontrada(s)`); }
    catch (e) { toast.error("QZ Tray não encontrado. Instale e abra o QZ Tray neste computador."); }
    finally { setSearching(false); }
  };
  useEffect(() => { if (cfg.method === "qz") search(); }, [cfg.method]); // eslint-disable-line react-hooks/exhaustive-deps

  const test = async () => {
    const { data } = await api.get("/settings");
    printOrder(SAMPLE, data).catch((e) => toast.error(e.message || "Falha ao imprimir"));
  };
  const printerOptions = [...new Set([cfg.printer, ...printers].filter(Boolean))];

  return (
    <div className={card}>
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Printer className="h-5 w-5 text-primo-400" /> Impressora de pedidos</h2>
      <p className="text-xs text-slate-500">Configuração salva neste computador/navegador.</p>
      <div><Label>Modo de impressão</Label>
        <Select value={cfg.method} onValueChange={(v) => update({ method: v })}>
          <SelectTrigger data-testid="print-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="browser" data-testid="print-method-browser">Navegador (janela de impressão do sistema)</SelectItem>
            <SelectItem value="qz" data-testid="print-method-qz">QZ Tray (impressão direta)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {cfg.method === "qz" && (
        <div className="space-y-2">
          <Label>Impressora instalada</Label>
          <div className="flex gap-2">
            <Select value={cfg.printer || undefined} onValueChange={(v) => update({ printer: v })}>
              <SelectTrigger data-testid="select-printer-dropdown"><SelectValue placeholder="Selecione a impressora" /></SelectTrigger>
              <SelectContent>{printerOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" variant="secondary" data-testid="search-printers-button" onClick={search} disabled={searching}><RefreshCw className={`h-4 w-4 ${searching ? "animate-spin" : ""}`} /></Button>
          </div>
          <p className="text-xs text-slate-500">Requer o <a href="https://qz.io/download/" target="_blank" rel="noreferrer" className="text-primo-400 underline">QZ Tray</a> instalado e aberto neste computador.</p>
        </div>
      )}
      {cfg.method === "browser" && <p className="text-xs text-slate-500">A impressora é escolhida na janela de impressão a cada pedido. Dica: no Chrome, a última impressora usada fica memorizada.</p>}
      <div><Label>Largura do papel</Label>
        <Select value={String(cfg.width)} onValueChange={(v) => update({ width: Number(v) })}>
          <SelectTrigger data-testid="paper-width-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="80" data-testid="paper-width-80">80mm</SelectItem>
            <SelectItem value="58" data-testid="paper-width-58">58mm</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-black/20 p-3">
        <div><p className="font-medium">Imprimir novos pedidos automaticamente</p><p className="text-xs text-slate-500">Mantenha o painel aberto neste computador</p></div>
        <Switch data-testid="auto-print-switch" checked={cfg.autoPrint} onCheckedChange={(v) => update({ autoPrint: v })} />
      </div>
      <Button type="button" data-testid="test-print-button" onClick={test} className="rounded-full font-bold">Imprimir cupom de teste</Button>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Configurações</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Loja & Impressora</h1>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <StoreSettings />
        <PrinterSettings />
      </div>
    </div>
  );
}
